import {
  Box,
  Card,
  Pagination,
  usePagination,
  useUpdateQueryParams,
  BodyText,
  AccountFundingOption,
  AccountListingType,
  ClientBankAccountsLists,
  EmbedUrlRequest,
  FetchFundingOptionsResponse,
  TableProps,
  toBool,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PaymentMethodCard from '../../UI/PaymentMethodCard/PaymentMethodCard';
import DefaultFundingMethods, { CREDIT_CARD_CURRENCY } from './DefaultFundingMethods/DefaultFundingMethods';
import BankSearchForm from './BankSearchForm';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useAccountInfo } from '@/providers/AccountInfo.provider';
import useFetchDefaultPaymentMethod from '@/hooks/useApi/useFetchDefaultPaymentMethod';
import useFetchDefaultBankAccount from '@/hooks/useApi/useFetchDefaultBankAccount';
import usePostSetDefaultPaymentMethod from '@/hooks/useApi/usePostSetDefaultPaymentMethod';
import usePostSetMyClientBankAccount from '@/hooks/useApi/usePostSetMyClientBankAccount';
import usePostSetMyClientCreditCard from '@/hooks/useApi/usePostSetMyClientCreditCard';
import useFetchDefaultCreditCard from '@/hooks/useApi/useFetchDefaultCreditCard';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import styles from './BankAccount.module.scss';
import { ELinxConnect, EventMessage } from 'shared/eLinx';
import AccountContext, { AccountCtx } from './Dialogue/AccountContext';
import AccountDialogue from './Dialogue/AccountDialogue';
import { AccountDialogueHandler, DialogueHandlerProps } from './Dialogue/types';
import AccountDetailsDrawer from './Details/AccountDetailsDrawer';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useToast } from '@/providers/Toast.provider';
import { MyAccountKeyAndValue, MyAccountType } from './types';

import { Button, TableFiltersContainer, Table, Title, UtilButton } from 'UI';

type QueryParams = {
  type: string;
  currency: string;
  searchTerm: string;
};

type AccountBillingSearchParams = {
  type?: string;
  currency?: string;
  searchTerm?: string;
};

type MyAccountTableRow = MyAccountKeyAndValue & { index: number };

type DefaultSave = {
  type: 'bank' | 'credit' | 'vopay';
  token: string;
};
type AccountListType = { [idx: number]: MyAccountKeyAndValue };

type AccountType = {
  AccountListingType?: AccountListingType;
};

type FC = FunctionComponent<AccountType>;

function getAccountOptionLabel(account: MyAccountKeyAndValue): string {
  return account.Iban || account.AccountNumber || account.CreditCardNumber || account.MaskedAccount || '';
}

function getAccountLabel(account: MyAccountKeyAndValue): string {
  if (account.Iban) return account.Iban;

  if (account.AccountNumber) {
    if (account.Country === 'CA') {
      return `${account.FinancialInstitutionNumber || ''}-${account.BranchTransitNumber || ''}-${account.AccountNumber}`;
    }

    if (account.Country === 'US') {
      return `${account.ABARoutingNumber || ''}-${account.AccountNumber}`;
    }

    return account.AccountNumber;
  }

  return account.CreditCardNumber || account.MaskedAccount || '';
}

const BankAccountsContainer: FC = ({ AccountListingType = 'Operational' }: AccountType) => {
  const { userSettings } = useUserSettingsProvider();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'Accounts.MyBankAccounts.BankAccountsContainer' });
  const { t: tGlobal } = useTranslation('translations');
  const history = useHistory();
  const toast = useToast();
  const { response } = useClientData();
  const { getModuleTitle } = useNavMenu();
  const [isShowElinx, setIsShowElinx] = useState(false);
  const [selectedOperationalAccount, setSelectedOperationalAccount] = useState<MyAccountTableRow | null>(null);
  const [defaultPaymentMethodResponse, getDefaultPaymentMethod] = useFetchDefaultPaymentMethod({});
  const [defaultBankAccountResponse, getDefaultBankAccount] = useFetchDefaultBankAccount({});
  const [setDefaultPaymentMethodResponse, setDefaultPaymentMethod] = usePostSetDefaultPaymentMethod({});
  const [setDefautlBankAccountResponse, setDefautlBankAccount] = usePostSetMyClientBankAccount({});
  const [setDefaultCreditCardResponse, setDefaultCreditCard] = usePostSetMyClientCreditCard({});

  const {
    defaultOperationalCreditCard,
    fetching: isFetchingDefaultCreditCard,
    fetchDefaultCreditCard,
  } = useFetchDefaultCreditCard();

  const { fetching: isFetchingFundingOptions, fetchFundingOptions } = useFetchFundingOptions();
  const [fundingOptions, setFundingOptions] = useState<AccountFundingOption[]>([]);
  const { permissions } = usePermissions();
  const [filteredLists, setFilteredLists] = useState<AccountListType>({});
  const [accountListsAll, setAccountListsAll] = useState<AccountListType>({});
  const currentLocation = useLocation<{ name: string; type: string; isDelete: boolean } | undefined>();
  const { state } = currentLocation;
  const location = useLocation();

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      type: params.get('type') || '',
      currency: params.get('currency') || '',
      searchTerm: params.get('searchTerm') || '',
    };
  }, [location.search]);

  const type = useMemo(() => queryParams.type || '', [queryParams]);
  const currency = useMemo(() => queryParams.currency || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);

  const filtersApplied = useMemo(() => {
    return Object.values(queryParams).some((val) => val !== '');
  }, [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { setOpenModule } = useOpenModule();

  const { creditCards, fetchCreditCards } = useFetchClientCreditCards();

  const [bankAccountsRes, fetchClientBankAccounts] = useFetchClientBankAccounts({});
  const { setNewCurrencyInfo } = useAccountInfo();
  const [invalidBankAccountImages, setInvalidBankAccountImages] = useState<number[]>([]);
  const [invalidCreditCardImages, setInvalidCreditCardImages] = useState<number[]>([]);

  // pagination
  const totalDataLength = useMemo(() => {
    return Object.values(filteredLists).length;
  }, [filteredLists]);

  const PER_PAGE = 20;

  const { pageCount, pageIndex, offset, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const [showDeleteAccountConfirmAlert, setShowDeleteAccountConfirmAlert] = useState(false);
  const [showDeleteAccountErrorAlert] = useState(false);
  const [showDeleteAccountSuccessAlert] = useState(false);
  const [deleteAccountError] = useState('');
  const [showChangeDefualtBilling, setShowChangeDefualtBilling] = useState(false);
  const [showChangeDefualtOperational, setShowChangeDefualtOperational] = useState(false);
  const [showDeleteBillingConfirmAlert, setShowDeleteBillingConfirmAlert] = useState(false);
  const [showChangeDefualtOperationalSuccessfull, setShowChangeDefualtOperationalSuccessfull] = useState(false);
  const [showChangeDefualtBillingSuccessfull, setShowChangeDefualtBillingSuccessfull] = useState(false);
  const [showAddTypeOfOperational, setShowAddTypeOfOperational] = useState(false);
  const [showAddTypeOfBilling, setShowAddTypeOfBilling] = useState(false);
  const [showAddOperational, setShowAddOperational] = useState(false);
  const [showAddBilling, setShowAddBilling] = useState(false);
  const [showAddOperationalSuccessfull, setShowAddOperationalSuccessfull] = useState(false);
  const [showAddBillingSuccessfull, setShowAddBillingSuccessfull] = useState(false);
  const [accountType, setAccountType] = useState('');
  const [accontNumber, setAccontNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [defaultSave, setDefaultSave] = useState<DefaultSave>();
  const [currencySelected, setCurrencySelected] = useState('');
  const [saveingDefault, setSavingDefult] = useState(false);
  const [connectedElinx, setConnectedElinx] = useState(false);
  const [tokenTypeBank, setTokenTypeBank] = useState<'bank' | 'credit'>('bank');
  const [paymentMethodOperational, setPaymentMethodOperational] = useState<string>('bankAccount');

  const [isAdding, setIsAdding] = useState(false);
  const [tokenSelectedBank, setTokenSelectedBank] = useState('');
  const [triggerAddOperational, setTriggerAddOperational] = useState(false);
  const [triggerAddBilling, setTriggerAddBilling] = useState(false);
  const [selectableBankAccounts, setSelectableBankAccounts] = useState<MyAccountKeyAndValue[]>([]);
  const [changeDefaultToast, setChangeDefualtToast] = useState(false);

  const DeleteAccount: AccountCtx = {
    showDeleteAccountConfirmAlert,
    showDeleteAccountErrorAlert,
    showDeleteAccountSuccessAlert,
    showDeleteBillingConfirmAlert,
    showChangeDefualtBilling,
    showChangeDefualtOperational,
    deleteAccountError,
    showChangeDefualtOperationalSuccessfull,
    showChangeDefualtBillingSuccessfull,
    showAddTypeOfOperational,
    showAddTypeOfBilling,
    showAddOperational,
    showAddBilling,
    showAddOperationalSuccessfull,
    showAddBillingSuccessfull,
    accontNumber,
    accountType: tGlobal([
      `Accounts.MyBankAccounts.accountTypeItem.${accountType}`,
      'Accounts.MyBankAccounts.accountTypeItem.Bank Account',
    ]),
    accountName,
    currency: currencySelected,
    selectableAccounts: selectableBankAccounts,
    selectionChanged: setTokenSelectedBank,
    token: tokenSelectedBank,
    paymentMehtod: paymentMethodOperational,
    paymentMethodChanged: setPaymentMethodOperational,
  };

  const [elinxConnectSettings, setElinxConnectSettings] = useState<EmbedUrlRequest>({
    RedirectURL: 'https://vopay.com',
    Country: response.Country,
    PaymentSelectionMethod: AccountListingType === 'Operational' ? 'bank,credit' : 'bank,credit',
    ClientControlled: true,
    RedirectMethod: 'JavascriptMessage',
    Language: i18n.language,
  });

  const setAccountDefault = (asDefault: boolean, defaultType?: 'vopay' | 'bank' | 'credit', token2?: string) => {
    if (defaultType === 'bank') {
      // set my bank account
      setDefautlBankAccount({
        PortalUser: response.LoginUserName,
        Token: token2 || '',
        SetAsDefault: asDefault,
        Purpose: AccountListingType.toLowerCase(),
      });
    }

    if (defaultType === 'credit') {
      // credit card set default
      setDefaultCreditCard({
        CreditCardToken: token2 || '',
        SetAsDefault: asDefault,
        Purpose: AccountListingType,
      });
    }

    if (AccountListingType === 'Billing' && asDefault) {
      setDefaultPaymentMethod({
        PortalUser: response.LoginUserName,
        PaymentMethod: defaultType || 'bank',
      });
    }
  };

  useEffect(() => {
    fetchCreditCards({
      PortalUser: response.LoginUserName,
      ClientControlled: true,
    });
  }, []);

  useEffect(() => {
    if (AccountListingType !== 'Operational') return;

    const loadDefaultCreditCard = async () => {
      try {
        await fetchDefaultCreditCard();
      } catch {
        toast.neutral(
          t('FundingMethod.fetchError', {
            defaultValue: 'Could not load default funding methods.',
          }),
        );
      }
    };

    loadDefaultCreditCard();
  }, [AccountListingType]);

  useEffect(() => {
    if (changeDefaultToast) {
      setChangeDefualtToast(false);
    }
  }, [changeDefaultToast]);

  useEffect(() => {
    if (paymentMethodOperational === 'bankAccount' && AccountListingType === 'Operational') {
      setElinxConnectSettings({
        RedirectURL: 'https://vopay.com',
        Country: response.Country,
        PaymentSelectionMethod: 'bank',
        ClientControlled: true,
        RedirectMethod: 'JavascriptMessage',
        Language: i18n.language,
      });
    }

    if (paymentMethodOperational === 'creditCard' && AccountListingType === 'Operational') {
      setElinxConnectSettings({
        RedirectURL: 'https://vopay.com',
        Country: response.Country,
        PaymentSelectionMethod: 'credit',
        ClientControlled: true,
        RedirectMethod: 'JavascriptMessage',
        Language: i18n.language,
      });
    }
  }, [paymentMethodOperational]);

  useEffect(() => {
    if (state?.isDelete) {
      const newState = { ...state, isDelete: false };

      history.replace({
        ...currentLocation,
        state: newState,
      });
    }
  }, [state]);

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountDialogueHandler = {
        delete: {},
        confirm: {
          confirm: {
            default: () => {
              setShowDeleteAccountConfirmAlert(false);
              setShowDeleteBillingConfirmAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowDeleteAccountConfirmAlert(false);
              setShowDeleteBillingConfirmAlert(false);
            },
          },
        },
        changeDefault: {
          confirm: {
            default: () => {
              setSavingDefult(true);
              setIsAdding(false);
              setAccountDefault(true, defaultSave?.type, defaultSave?.token);
            },
          },
          fail: {
            default: () => {
              if (AccountListingType === 'Operational') {
                setShowChangeDefualtOperational(false);
              } else {
                setShowChangeDefualtBilling(false);
              }
            },
          },
        },
        successDefaultOperational: {
          confirm: {
            default: () => {
              setShowChangeDefualtOperationalSuccessfull(false);
            },
          },
        },
        successDefaultBiliing: {
          confirm: {
            default: () => {
              setShowChangeDefualtBillingSuccessfull(false);
            },
          },
        },
        successAddOperational: {
          confirm: {
            default: () => {
              setShowAddOperationalSuccessfull(false);
            },
          },
        },
        successAddBiliing: {
          confirm: {
            default: () => {
              setShowAddBillingSuccessfull(false);
            },
          },
        },
        operationalType: {
          add: {
            default: () => {
              // code to just add
              setSavingDefult(false);
              setIsAdding(true);
              setAccountDefault(false, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfOperational(false);
            },
          },
          setDefault: {
            default: () => {
              // code to default and add
              setSavingDefult(true);
              setIsAdding(true);
              setAccountDefault(true, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfOperational(false);
            },
          },
          close: {
            default: () => {
              setShowAddTypeOfOperational(false);
            },
          },
        },
        billingType: {
          add: {
            default: () => {
              // code to just add
              setSavingDefult(false);
              setIsAdding(true);
              setAccountDefault(false, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfBilling(false);
            },
          },
          setDefault: {
            default: () => {
              // code to just add
              setSavingDefult(true);
              setIsAdding(true);
              setAccountDefault(true, tokenTypeBank, tokenSelectedBank);
              setShowAddTypeOfBilling(false);
            },
          },
          close: {
            default: () => {
              setShowAddTypeOfBilling(false);
            },
          },
        },
        addBilling: {
          add: {
            default: () => {
              // set save operational iframe
              setShowAddBilling(false);
              setIsShowElinx(true);
            },
          },
          addAccount: {
            default: () => {
              // code for setting type
              setTriggerAddBilling(true);
            },
          },
          close: {
            default: () => {
              setShowAddBilling(false);
            },
          },
        },
        addOperational: {
          add: {
            default: () => {
              // set save operational or ther on iframe
              setShowAddOperational(false);
              setIsShowElinx(true);
            },
          },
          addAccount: {
            default: () => {
              // code for setting type
              setTriggerAddOperational(true);
            },
          },
          close: {
            default: () => {
              setPaymentMethodOperational('bankAccount');
              setShowAddOperational(false);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  useEffect(() => {
    if (triggerAddOperational || triggerAddBilling) {
      const account = Object.values(accountListsAll).find(
        (x) => x.Token === tokenSelectedBank || x.CreditCardToken === tokenSelectedBank,
      );

      setCurrencySelected(account?.Currency || 'CAD');
      setAccountType(account?.Type || 'Bank Account');

      setAccontNumber(
        account?.Iban || account?.AccountNumber || account?.CreditCardNumber || account?.MaskedAccount || '',
      );

      setAccountName(
        account?.AccountHolderName
          ? account.AccountHolderName
          : account?.RecipientName
            ? account.RecipientName
            : account?.CreditCardHolderName
              ? account.CreditCardHolderName
              : '',
      );

      setDefaultSave({
        type: account?.Type === 'Credit Card' ? 'credit' : 'bank',
        token: account?.Token || account?.CreditCardToken || '',
      });

      if (tokenSelectedBank !== '') {
        if (triggerAddBilling) {
          setShowAddBilling(false);
          setShowAddTypeOfBilling(true);
        }

        if (triggerAddOperational) {
          setShowAddOperational(false);
          setShowAddTypeOfOperational(true);
        }
      }

      if (triggerAddBilling) {
        setTriggerAddBilling(false);
      }

      if (triggerAddOperational) {
        setTriggerAddOperational(false);
      }
    }
  }, [triggerAddOperational, triggerAddBilling]);

  useEffect(() => {
    refreshBankAccounts();

    getDefaultPaymentMethod({
      PortalUser: response.LoginUserName,
    });

    getDefaultBankAccount({
      PortalUser: response.LoginUserName,
      Timezone: userSettings.timezone,
      ...(currency === 'All' ? {} : { Currency: currency }),
    });
  }, []);

  async function refreshBankAccounts() {
    fetchClientBankAccounts({
      PortalUser: response.LoginUserName,
      ClientControlled: true,
      Token: '',
      Timezone: userSettings.timezone,
    });
  }

  useEffect(() => {
    if (bankAccountsRes.Success) {
      // TODO: fetch credit cards on demand (NOT when loading the app)
      const bankAccounts = bankAccountsRes.BankAccounts;

      const myAccounts: MyAccountKeyAndValue[] = Object.values(bankAccounts)
        .filter((account) => account.Purpose?.split(',').includes(AccountListingType))
        .map((bankAccount: ClientBankAccountsLists) => ({
          ...bankAccount,
          Type: 'Bank Account',
          Country: response.Country,
          Province: bankAccount['Province/State'],
          PostalCode: bankAccount['PostalCode/ZipCode'],
          IsDefaultAccount: toBool(bankAccount.IsDefault),
          IsDefault:
            (AccountListingType === 'Operational'
              ? Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount).some(
                  (obj) => obj.Token === bankAccount.Token || '',
                )
              : Object.values(defaultBankAccountResponse.DefaultBillingBankAccount).some(
                  (obj) => obj.Token === bankAccount.Token,
                )) &&
            (AccountListingType === 'Operational' || defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank'),
          IsOperationalDefault: Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount).some(
            (obj) => obj.Token === bankAccount.Token || '',
          ),
          IsBillingDefault: Object.values(defaultBankAccountResponse.DefaultBillingBankAccount).some(
            (obj) => obj.Token === bankAccount.Token,
          ),
        }));

      const myAccountsOther: MyAccountKeyAndValue[] = Object.values(bankAccounts)
        .filter((account) => !account.Purpose?.split(',').includes(AccountListingType))
        .map((bankAccount: ClientBankAccountsLists) => ({
          ...bankAccount,
          Type: 'Bank Account',
          Country: response.Country,
          IsDefault: toBool(bankAccount.IsDefault),
        }));

      const myCards: MyAccountKeyAndValue[] = creditCards.map((creditCard) => {
        const isOperationalDefault =
          AccountListingType === 'Operational' &&
          !!creditCard.CreditCardToken &&
          creditCard.CreditCardToken === defaultOperationalCreditCard?.CreditCardToken;

        return {
          ...creditCard,
          Type: 'Credit Card',
          Country: response.Country,
          IsDefault:
            isOperationalDefault ||
            (defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit' &&
              AccountListingType === 'Billing' &&
              creditCard.IsDefault === '1'),
          IsOperationalDefault: isOperationalDefault,
          IsBillingDefault:
            defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit' && creditCard.IsDefault === '1',
          IsDefaultAccount: toBool(creditCard.IsDefault),
          Currency: 'CAD',
        };
      });

      const mergedContacts: AccountListType = {};

      myAccounts.concat(myCards).forEach((contacts, index) => {
        mergedContacts[index] = { ...contacts };
      });

      const mergedContacts2: AccountListType = {};

      myAccountsOther.concat(myCards).forEach((contacts, index) => {
        mergedContacts2[index] = { ...contacts };
      });

      const mergedContacts3: AccountListType = {};

      myAccountsOther
        .concat(myCards)
        .concat(myAccounts)
        .forEach((contacts, index) => {
          mergedContacts3[index] = { ...contacts };
        });

      if (connectedElinx) {
        const selectedAccount = Object.values(mergedContacts3).find(
          (x) => x.Token === tokenSelectedBank || x.CreditCardToken === tokenSelectedBank,
        );

        if (selectedAccount) {
          if (AccountListingType === 'Billing') {
            setTriggerAddBilling(true);
          } else if (selectedAccount.Type === 'Credit Card') {
            setAccountType(selectedAccount.Type);
            setAccountName(selectedAccount.CreditCardHolderName || '');

            setTriggerAddOperational(true);
          } else {
            setTriggerAddOperational(true);
          }

          setConnectedElinx(false);
        }
      }

      setSelectableBankAccounts(
        Object.values(mergedContacts2).filter(
          (x) => !x.Purpose?.split(',').includes(AccountListingType) && x.Type === 'Bank Account',
        ),
      );

      const searchParams = {
        type,
        currency,
        searchTerm,
      };

      const keys = ['AccountHolderName', 'CreditCardHolderName', 'FirstName', 'LastName', 'FullName', 'RecipientName'];

      setFilteredLists(
        Object.values(mergedContacts || {}).filter(
          (data) =>
            (searchParams.type === '' ||
              searchParams.type === 'All' ||
              data.Type.replace(/\s+/g, '') === searchParams.type) &&
            (searchParams.searchTerm === '' ||
              keys.some((key) =>
                (data[key as keyof MyAccountKeyAndValue] as string | undefined)
                  ?.toLowerCase()
                  .includes(searchParams.searchTerm || ''?.toLowerCase()),
              )),
        ),
      );

      setAccountListsAll(mergedContacts3);

      // keep the open drawer's account in sync with the refreshed list
      setSelectedOperationalAccount((prev) => {
        if (!prev) return prev;

        const refreshed = Object.values(mergedContacts3).find(
          (x) =>
            (!!x.Token && x.Token === prev.Token) ||
            (!!x.CreditCardToken && x.CreditCardToken === prev.CreditCardToken),
        );

        return refreshed ? { ...refreshed, index: prev.index } : prev;
      });
    }
  }, [
    creditCards,
    defaultOperationalCreditCard,
    defaultPaymentMethodResponse,
    defaultBankAccountResponse,
    bankAccountsRes,
    bankAccountsRes.BankAccounts,
    connectedElinx,
    AccountListingType,
    response.Country,
    tokenSelectedBank,
    type,
    currency,
    searchTerm,
  ]);

  useEffect(() => {
    getDefaultPaymentMethod({
      PortalUser: response.LoginUserName,
    });
  }, [setDefaultPaymentMethodResponse]);

  // TODO ────────────────────────────────────────────────────────────────────
  // 1. Surface set-as-default failures to the user — currently silent.
  //    This effect and the credit-card one below only handle
  //    Success: true. On Success: false nothing happens — no error surfaced,
  //    confirm dialogue stays open. Surface the backend ErrorMessage and
  //    close/keep the dialogue deliberately (applies to all four call sites:
  //    bank/credit × Billing/Operational).
  // 2. Dead state: changeDefaultToast is set true here and immediately reset
  //    false by the effect near the top of this component; nothing renders it.
  //    Delete it or wire an actual success toast.
  // 3. Stuck fullscreen loader after an in-page Search: search() fires both
  //    fetchClientBankAccounts and getDefaultBankAccount; the second request
  //    is raced by its own legacy hook's CancelToken cleanup and, when
  //    cancelled, the hook's awaited call rejects after it has already set the
  //    fullscreen indicator on — setDataFetchingInfo(false) never runs and the
  //    loader modal blocks the page until reload. Migrating these hooks to the
  //    Direct Pattern (try/finally around the indicator) fixes it. The Cypress
  //    scenario "Switching tabs clears the payment method filters" deep-links
  //    its filter to avoid this; restore its search→tab-switch variant then.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (setDefautlBankAccountResponse.Success) {
      refreshBankAccounts();
      setShowChangeDefualtBilling(false);
      setShowChangeDefualtOperational(false);
      setShowAddTypeOfBilling(false);
      setShowAddTypeOfOperational(false);

      if (!saveingDefault) {
        if (AccountListingType === 'Operational') {
          setShowAddOperationalSuccessfull(true);
        } else {
          setShowAddBillingSuccessfull(true);
        }
      } else if (isAdding) {
        if (AccountListingType === 'Operational') {
          setShowChangeDefualtOperationalSuccessfull(true);
        } else {
          setShowChangeDefualtBillingSuccessfull(true);
        }
      } else if (AccountListingType === 'Operational') {
        setChangeDefualtToast(true);
      } else {
        setChangeDefualtToast(true);
      }

      getDefaultBankAccount({
        PortalUser: response.LoginUserName,
        Timezone: userSettings.timezone,
        ...(currency === 'All' ? {} : { Currency: currency }),
      });
    }
  }, [setDefautlBankAccountResponse]);

  useEffect(() => {
    if (setDefaultCreditCardResponse.Success) {
      fetchCreditCards({
        PortalUser: response.LoginUserName,
        ClientControlled: true,
      });

      setShowChangeDefualtBilling(false);
      setShowChangeDefualtOperational(false);
      setShowAddTypeOfBilling(false);
      setShowAddTypeOfOperational(false);

      if (!saveingDefault) {
        if (AccountListingType === 'Operational') {
          setShowAddOperationalSuccessfull(true);
        } else {
          setShowAddBillingSuccessfull(true);
        }
      } else if (isAdding) {
        if (AccountListingType === 'Operational') {
          setShowChangeDefualtOperationalSuccessfull(true);
        } else {
          setShowChangeDefualtBillingSuccessfull(true);
        }
      } else if (AccountListingType === 'Operational') {
        setChangeDefualtToast(true);
      } else {
        setChangeDefualtToast(true);
      }

      getDefaultBankAccount({
        PortalUser: response.LoginUserName,
        Timezone: userSettings.timezone,
        ...(currency === 'All' ? {} : { Currency: currency }),
      });

      if (AccountListingType === 'Operational') {
        // Purpose sent above was scoped to this listing type, so a Billing-page change
        // can't affect the Operational default — and only Operational renders it.
        fetchDefaultCreditCard().catch(() => {
          toast.neutral(
            t('FundingMethod.fetchError', {
              defaultValue: 'Could not load default funding methods.',
            }),
          );
        });
      }
    }
  }, [setDefaultCreditCardResponse]);

  const search = (searchParams: AccountBillingSearchParams = {}) => {
    const defaultValues = {
      PortalUser: response.LoginUserName,
      ClientControlled: true,
      Token: '',
      ...(searchParams.currency === 'All' ? {} : { Currency: searchParams.currency }),
      SearchText: searchParams.searchTerm,
    };

    fetchClientBankAccounts(defaultValues);

    getDefaultBankAccount({
      PortalUser: response.LoginUserName,
      ...(searchParams.currency === 'All' ? {} : { Currency: searchParams.currency }),
    });
  };

  useEffect(() => {
    setPageIndex(0);

    const searchParams = {
      type,
      currency,
      searchTerm,
    };

    updateQueryParams(searchParams);
    search(searchParams);
  }, []);

  const handleBankAccountImageError = (rowIndex: number) => {
    setInvalidBankAccountImages((prevInvalidImages) => [...prevInvalidImages, rowIndex]);
  };

  const handleCreditCardImageError = (rowIndex: number) => {
    setInvalidCreditCardImages((prevInvalidImages) => [...prevInvalidImages, rowIndex]);
  };

  const tableProps: TableProps<MyAccountTableRow> = {
    id: 'my-bank-account-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'account',
        header: t('Table.account', { defaultValue: 'Account' }) || '',
        align: 'left',
        template: ({ data }) => {
          const accountLabel = getAccountLabel(data);

          return (
            <Box flx="col">
              <Box flx="row" ai="center">
                {data.BankId !== null &&
                  !invalidBankAccountImages.includes(data.index) &&
                  data.Type === 'Bank Account' && (
                    <img
                      alt="logo"
                      onError={() => handleBankAccountImageError(data.index)}
                      src={`https://embed-dev.vopay.com/images/logos-banks/${data.BankId}.svg`}
                    />
                  )}

                {data.CreditCardBrand !== null &&
                  !invalidCreditCardImages.includes(data.index) &&
                  data.Type === 'Credit Card' && (
                    <img
                      alt="logo"
                      onError={() => handleCreditCardImageError(data.index)}
                      src={`https://embed-dev.vopay.com/images/${data.CreditCardBrand}.png`}
                    />
                  )}

                <UtilButton size="medium" onClick={() => setSelectedOperationalAccount(data)}>
                  {accountLabel}
                </UtilButton>
              </Box>
            </Box>
          );
        },
      },
      {
        id: 'clientName',
        header: t('Table.accountHolder', { defaultValue: 'Account Holder' }) || '',
        align: 'left',
        template: ({ data }) => data.AccountHolderName || data.RecipientName || data.CreditCardHolderName || '',
      },
      {
        id: 'type',
        header: t('Table.accountType', { defaultValue: 'Account Type' }) || '',
        align: 'left',
        template: ({ data }) =>
          data.Type
            ? tGlobal(`Accounts.MyBankAccounts.accountTypeItem.${data.Type}`)
            : tGlobal('Accounts.MyBankAccounts.accountTypeItem.Bank Account'),
      },
      {
        id: 'currency',
        header: t('Table.currency', { defaultValue: 'Currency' }) || '',
        align: 'left',
        field: 'Currency',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => {
          const isDefault =
            ((AccountListingType === 'Operational'
              ? Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount).some(
                  (obj) => obj.Token === data.Token,
                )
              : Object.values(defaultBankAccountResponse.DefaultBillingBankAccount).some(
                  (obj) => obj.Token === data.Token,
                )) &&
              (AccountListingType === 'Operational' || defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank')) ||
            (data.Type === 'Credit Card' &&
              data.IsDefault &&
              AccountListingType === 'Billing' &&
              defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit');

          return (
            <Box gap="sm" jc="fe">
              {!isDefault &&
                (!data.IsDefaultAccount ||
                  data.IsBillingDefault ||
                  data.IsOperationalDefault ||
                  data.Type !== 'Bank Account') &&
                (data.Type === 'Bank Account' || (data.Type === 'Credit Card' && AccountListingType === 'Billing')) && (
                  <Button
                    id={`account-detail-${data.index}`}
                    size="small"
                    disabled={
                      (AccountListingType === 'Billing' && !permissions.ManageAccountandBilling) ||
                      (AccountListingType === 'Operational' && !permissions.ManageDefaultPayments)
                    }
                    onClick={() => {
                      setCurrencySelected(data.Currency);
                      setAccountType(data.Type);
                      setAccontNumber(getAccountOptionLabel(data));
                      setAccountName(data.AccountHolderName || data.RecipientName || data.CreditCardHolderName || '');

                      if (AccountListingType === 'Operational') {
                        setShowChangeDefualtOperational(true);
                      } else {
                        setShowChangeDefualtBilling(true);
                      }

                      setDefaultSave({
                        type: data.Type === 'Credit Card' ? 'credit' : 'bank',
                        token: data.Token || data.CreditCardToken || '',
                      });
                    }}
                  >
                    {AccountListingType === 'Operational'
                      ? t('Table.setAsDefaultOperational', { defaultValue: 'Set as Default Operational' })
                      : t('Table.setAsDefaultBilling', { defaultValue: 'Set as Default Billing' })}
                  </Button>
                )}
            </Box>
          );
        },
      },
    ],
    rows: Object.values(filteredLists || {})
      .sort((a, b) => (a.IsDefault === b.IsDefault ? 0 : a.IsDefault ? -1 : 1))
      .map((item, index) => ({
        id: item.Token || item.CreditCardToken || item.ID || String(Math.random()),
        data: { ...item, index },
      }))
      .slice(offset, offset + PER_PAGE),
  };

  // GET /account/funding-options is scoped to a single currency per call, so fetch once per
  // currency actually shown (each bank account's currency, plus CAD for the credit card) and
  // merge the results.
  const fundingOptionCurrencies = useMemo(() => {
    const currencies = Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount || {}).map(
      (account) => account.Currency,
    );

    if (response.FundMyAccountCreditCardEnabled) currencies.push(CREDIT_CARD_CURRENCY);

    return Array.from(new Set(currencies));
  }, [defaultBankAccountResponse, response.FundMyAccountCreditCardEnabled]);

  const fundingOptionCurrenciesKey = fundingOptionCurrencies.join(',');

  useEffect(() => {
    if (AccountListingType !== 'Operational' || fundingOptionCurrencies.length === 0) return;

    const loadFundingOptions = async () => {
      const results = await Promise.allSettled(
        fundingOptionCurrencies.map((currency) => fetchFundingOptions({ Currency: currency })),
      );

      const succeeded = results.filter(
        (result): result is PromiseFulfilledResult<FetchFundingOptionsResponse> => result.status === 'fulfilled',
      );

      setFundingOptions(succeeded.flatMap((result) => result.value.FundingOptions));

      if (succeeded.length < results.length) {
        toast.neutral(t('FundingMethod.fetchOptionsError', { defaultValue: 'Could not load funding fee details.' }));
      }
    };

    loadFundingOptions();
  }, [fundingOptionCurrenciesKey, AccountListingType]);

  const goToFundingPage = (pathname: string, token: string, type: MyAccountType, currency: string) => {
    setOpenModule('myAccount');
    setNewCurrencyInfo(currency);
    history.push({ pathname, state: { token, type, currency } });
  };

  const defaultAccountCardBanks = Object.values(defaultBankAccountResponse.DefaultBillingBankAccount || {})
    .sort((payment1, payment2) => payment1.Currency.localeCompare(payment2.Currency))
    .map((payment) => (
      <PaymentMethodCard
        key={payment.Token}
        Title={t('Card.billingDefault', { currency: payment.Currency, defaultValue: '{{currency}} Billing Default' })}
        Description={t('Card.billingDescription', {
          currency: payment.Currency,
          defaultValue: 'Account that is paying the {{currency}} monthly fees on your invoice',
        })}
        AccountNumber={payment.AccountNumber}
        AccountHolder={payment.AccountHolderName}
        ConnectionType={payment.Type === 'manual' ? 'manual' : 'online'}
        Expiry=""
        Type="bank"
        BankId={payment.BankId}
        CreditCardBrand=""
      />
    ));

  const defaultAccountCardCreditCard = creditCards
    .filter((x) => x.IsDefault === '1')
    .map((payment) => (
      <PaymentMethodCard
        key={payment.CreditCardToken}
        Title={t('Card.billingDefault', {
          currency: CREDIT_CARD_CURRENCY,
          defaultValue: '{{currency}} Billing Default',
        })}
        Description={t('Card.billingDescription', {
          currency: CREDIT_CARD_CURRENCY,
          defaultValue: 'Account that is paying the {{currency}} monthly fees on your invoice',
        })}
        AccountNumber={payment.CreditCardNumber}
        AccountHolder={payment.CreditCardHolderName}
        ConnectionType="online"
        Expiry={`20${payment.CreditCardExpiryYear}${'/'}${payment.CreditCardExpiryMonth}`}
        Type="credit"
        BankId=""
        CreditCardBrand={payment.CreditCardBrand}
      />
    ));

  const onClosePaymentMethod = () => {
    setIsShowElinx(false);
  };

  const onConnectPaymentMethod = (eventValues: EventMessage) => {
    setIsShowElinx(false);
    setTokenSelectedBank(eventValues.data.Token);

    if (eventValues.data.PaymentMethod === 'creditcard') {
      setTokenTypeBank('credit');
    } else if (eventValues.data.PaymentMethod === 'bank') {
      setTokenTypeBank('bank');
    }

    if (eventValues.data.PaymentMethod === 'creditcard' && AccountListingType === 'Operational') {
      setConnectedElinx(true);

      fetchCreditCards({
        PortalUser: response.LoginUserName,
        ClientControlled: true,
      });
    } else {
      setConnectedElinx(true);

      fetchCreditCards({
        PortalUser: response.LoginUserName,
        ClientControlled: true,
      });

      refreshBankAccounts();
    }
  };

  const MemoizedElinxConnect = useMemo(
    () => (
      <ELinxConnect setting={elinxConnectSettings} onSuccess={onConnectPaymentMethod} onClose={onClosePaymentMethod} />
    ),
    [elinxConnectSettings],
  );

  return (
    <AccountContext.Provider value={DeleteAccount}>
      {AccountListingType === 'Operational' && (
        <>
          <Box jc="sb" flx="row">
            <Box flx="col" mb="md" gapSize="8px" width="100%">
              <Title>{getModuleTitle()}</Title>

              <Box jc="sb" gapSize="80px">
                <BodyText>
                  {t('Description.operationalAccounts', {
                    defaultValue:
                      'Operational accounts are directly linked to your VoPay account. Set a default bank account and credit card per currency for funding and withdrawals.',
                  })}
                </BodyText>

                <Button
                  id="my-bank-accounts"
                  colorScheme="primary"
                  disabled={!permissions.ManageDefaultPayments}
                  onClick={() => {
                    setShowAddOperational(true);
                  }}
                >
                  {t('Button.addOperationalAccount', { defaultValue: 'Add Operational Account' })}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box mt="xl" mb="xl" flx="col" gapSize="16px">
            <Title level="4">{t('Section.defaultFundingMethod', { defaultValue: 'Default Funding Method' })}</Title>

            <DefaultFundingMethods
              defaultBankAccounts={Object.values(defaultBankAccountResponse.DefaultOperationalBankAccount || {})}
              defaultCreditCards={defaultOperationalCreditCard ? [defaultOperationalCreditCard] : []}
              fundingOptions={fundingOptions}
              isLoading={isFetchingFundingOptions || isFetchingDefaultCreditCard || !defaultBankAccountResponse.Success}
              isCreditCardFundingEnabled={response.FundMyAccountCreditCardEnabled}
              onClickFundBankAccount={
                response.SubAccountType === 'franchise'
                  ? undefined
                  : (account) => {
                      goToFundingPage('/fund-my-account', account.Token, 'Bank Account', account.Currency);
                    }
              }
              onClickWithdrawBankAccount={
                response.SubAccountType === 'franchise'
                  ? undefined
                  : (account) => {
                      goToFundingPage('/withdraw-my-account', account.Token, 'Bank Account', account.Currency);
                    }
              }
              onClickFundCreditCard={
                response.SubAccountType === 'franchise'
                  ? undefined
                  : (card) => {
                      goToFundingPage('/fund-my-account', card.CreditCardToken, 'Credit Card', 'CAD');
                    }
              }
              onClickContactSupport={() => {
                history.push({ pathname: '/tickets', state: { openCreateTicket: true } });
              }}
              onClickBankAccountDetails={(account) => {
                const fullAccount = Object.values(accountListsAll).find((x) => x.Token === account.Token);

                if (fullAccount) setSelectedOperationalAccount({ ...fullAccount, index: 0 });
              }}
              onClickCreditCardDetails={(card) => {
                const fullAccount = Object.values(accountListsAll).find(
                  (x) => x.CreditCardToken === card.CreditCardToken,
                );

                if (fullAccount) setSelectedOperationalAccount({ ...fullAccount, index: 0 });
              }}
            />
          </Box>

          <Box flx="col" gapSize="16px">
            <Title level="4">{t('Section.accounts', { defaultValue: 'Accounts' })}</Title>

            <Card style={{ padding: '16px' }}>
              <Box flx="col" gap="sm">
                <TableFiltersContainer filtersApplied={filtersApplied}>
                  <div style={{ marginTop: '16px', marginLeft: 'auto' }}>
                    <TableFiltersContainer.Trigger />
                  </div>

                  <TableFiltersContainer.Content>
                    <BankSearchForm
                      type={type}
                      currency={currency}
                      searchTerm={searchTerm}
                      search={(searchParams) => {
                        setPageIndex(0);

                        updateQueryParams({
                          type: searchParams.type || '',
                          currency: searchParams.currency || '',
                          searchTerm: searchParams.searchTerm || '',
                        });

                        search(searchParams);
                      }}
                    />
                  </TableFiltersContainer.Content>
                </TableFiltersContainer>
              </Box>

              <div className={styles.bankAccountTable}>
                <Table {...tableProps} />
              </div>

              <Box width="100%" jc="center" mt="lg" mb="sm">
                {pageCount > 1 && (
                  <Pagination
                    pageCount={pageCount}
                    forcePageNumber={pageIndex}
                    handlePageClick={(e) => {
                      setPageIndex(e.selected);
                    }}
                  />
                )}
              </Box>
            </Card>
          </Box>
        </>
      )}

      {AccountListingType === 'Billing' && (
        <>
          <Box jc="sb" flx="row">
            <div className={styles.billingContainer}>
              <Box height="auto">
                {defaultPaymentMethodResponse.DefaultPaymentMethod === 'bank' && defaultAccountCardBanks}
                {defaultPaymentMethodResponse.DefaultPaymentMethod === 'credit' && defaultAccountCardCreditCard}
              </Box>

              <Box jc="fe">
                <Button
                  id="contacts-addBank"
                  colorScheme="primary"
                  disabled={!permissions.ManageAccountandBilling}
                  onClick={() => {
                    if (toBool(response.ClientAccountsEnabled)) {
                      setIsShowElinx(true);
                    } else {
                      setShowAddBilling(true);
                    }
                  }}
                >
                  {t('Button.addBillingMethod', { defaultValue: 'Add Billing Method' })}
                </Button>
              </Box>
            </div>
          </Box>

          <Box flx="col" gap="sm">
            <TableFiltersContainer filtersApplied={filtersApplied}>
              <div style={{ marginTop: '16px', marginLeft: 'auto' }}>
                <TableFiltersContainer.Trigger />
              </div>

              <TableFiltersContainer.Content>
                <BankSearchForm
                  type={type}
                  currency={currency}
                  searchTerm={searchTerm}
                  search={(searchParams) => {
                    setPageIndex(0);

                    updateQueryParams({
                      type: searchParams.type || '',
                      currency: searchParams.currency || '',
                      searchTerm: searchParams.searchTerm || '',
                    });

                    search(searchParams);
                  }}
                />
              </TableFiltersContainer.Content>
            </TableFiltersContainer>
          </Box>

          <Box>
            <div className={styles.bankAccountTable}>
              <Table {...tableProps} />
            </div>
          </Box>

          <Box width="100%" jc="center" mt="lg" mb="sm">
            {pageCount > 1 && (
              <Pagination
                pageCount={pageCount}
                forcePageNumber={pageIndex}
                handlePageClick={(e) => {
                  setPageIndex(e.selected);
                }}
              />
            )}
          </Box>
        </>
      )}

      {isShowElinx && MemoizedElinxConnect}

      {createPortal(
        <AccountDialogue handleAccountDialogue={handlers.handleAccountDialogue} />,
        document.getElementById('modal-root')!,
      )}

      <AccountDetailsDrawer
        isOpen={selectedOperationalAccount !== null}
        onClose={() => setSelectedOperationalAccount(null)}
        account={selectedOperationalAccount}
        accountListingType={AccountListingType}
        onSetAsDefault={() => {
          if (!selectedOperationalAccount) return;
          const data = selectedOperationalAccount;

          setCurrencySelected(data.Currency);
          setAccountType(data.Type);
          setAccontNumber(getAccountOptionLabel(data));
          setAccountName(data.AccountHolderName || data.RecipientName || data.CreditCardHolderName || '');

          if (AccountListingType === 'Operational') {
            setShowChangeDefualtOperational(true);
          } else {
            setShowChangeDefualtBilling(true);
          }

          setDefaultSave({
            type: data.Type === 'Credit Card' ? 'credit' : 'bank',
            token: data.Token || data.CreditCardToken || '',
          });
        }}
        onDeleted={() => {
          if (selectedOperationalAccount?.Type === 'Credit Card') {
            fetchCreditCards({ PortalUser: response.LoginUserName, ClientControlled: true });
          } else {
            refreshBankAccounts();
          }
        }}
      />
    </AccountContext.Provider>
  );
};

export default BankAccountsContainer;
